import type { EndingType, Phase, StageStanding, TimeClass } from '@chess/api-model';
import { endingCaseView, endingResultView, middlegameResultView } from '@chess/api-ending-case';
import { groundCauseView } from '@chess/api-mistakes';
import type { DecidingStage, StageVerdict, VerdictFacts } from './types.ts';

/** The kind of ending as the start of a sentence; the labels (`ENDING_TYPE_LABEL`) are names, not phrases. */
const ENDING_PHRASE: Record<EndingType, string> = {
  pawn: 'A pawn ending',
  knight: 'A knight ending',
  'bishop-same': 'A bishop ending, the bishops on the same colour',
  'bishop-opposite': 'An ending of opposite-coloured bishops',
  'bishop-knight': 'A bishop against a knight',
  minor: 'A minor-piece ending',
  rook: 'A rook ending',
  'rook-minor': 'A rook and minor-piece ending',
  'rook-vs-minor': 'A rook against a minor piece',
  'double-rook': 'A double-rook ending',
  queen: 'A queen ending',
  'queen-vs-rook': 'A queen against a rook',
  'piece-vs-pawns': 'Pieces against pawns',
  mixed: 'A mixed-piece ending',
};

// The rule-based composer. Each stage's verdict is: the result in words,
// then what decided it (the cause and its move, the ending's kind), then the
// clock when it had a part — the game lost on time in that stage, a winning
// position let slip with seconds left (`timeSlip`), a middlegame lost to
// moves under a minute (`groundCause` time) — and, for the opening, the
// game against the reader's average in the same family. The pace of the
// game (bullet, blitz) is named only when the clock had a part, so the
// sentence says "in bullet" where it explains something.

/** Under this many seconds left, the clock is named. */
export const TIME_PRESSURE_SECONDS = 60;
/** A stage's score change must reach this many pawns to be named the deciding one. */
export const DECIDING_PAWNS = 1;

const STAGE_NAME: Record<Phase, string> = { opening: 'opening', middlegame: 'middlegame', ending: 'ending' };

/** The reader's score change across each stage the game reached, in pawns. */
export function stageChanges(facts: Pick<VerdictFacts, 'openingScore' | 'stageSwings'>): Partial<Record<Phase, number>> {
  const out: Partial<Record<Phase, number>> = {};
  if (facts.openingScore !== undefined) out.opening = facts.openingScore;
  if (facts.stageSwings?.middlegame !== undefined) out.middlegame = facts.stageSwings.middlegame;
  if (facts.stageSwings?.ending !== undefined) out.ending = facts.stageSwings.ending;
  return out;
}

/** The stage that decided the game: the largest score change, for or against the reader, of at least `DECIDING_PAWNS`. */
export function decidingStage(facts: Pick<VerdictFacts, 'scanned' | 'openingScore' | 'stageSwings'>): DecidingStage {
  if (!facts.scanned) return { kind: 'unscanned' };
  let best: { stage: Phase; pawns: number } | undefined;
  for (const [stage, pawns] of Object.entries(stageChanges(facts)) as [Phase, number][]) {
    if (!best || Math.abs(pawns) > Math.abs(best.pawns)) best = { stage, pawns };
  }
  return best && Math.abs(best.pawns) >= DECIDING_PAWNS ? { kind: 'stage', ...best } : { kind: 'even' };
}

const signed = (pawns: number) => `${pawns > 0 ? '+' : pawns < 0 ? '−' : ''}${Math.abs(pawns).toFixed(2)}`;

const RESULT: Record<VerdictFacts['outcome'], string> = { win: 'You won', draw: 'You drew', loss: 'You lost' };
const HOW: Partial<Record<VerdictFacts['ending'], string>> = { checkmate: 'by checkmate', resignation: 'by resignation', time: 'on time', abandoned: 'by abandonment', draw: '' };

/** The verdict on the whole game: the result, the accuracy against the usual, the stage that decided it, the clock. */
export function gameVerdict(facts: VerdictFacts): StageVerdict | undefined {
  if (!facts.scanned) return undefined;
  const how = facts.outcome === 'draw' ? '' : (HOW[facts.ending] ?? '');
  const result = `${RESULT[facts.outcome]}${how ? ` ${how}` : ''}${paceNote(facts.timeClass)}.`;
  let accuracy = '';
  if (facts.accuracy !== undefined) {
    const a = Math.round(facts.accuracy);
    const usual = facts.averageAccuracy === undefined ? '' : a - facts.averageAccuracy >= 3 ? `, above your usual ${Math.round(facts.averageAccuracy)}` : facts.averageAccuracy - a >= 3 ? `, below your usual ${Math.round(facts.averageAccuracy)}` : ', about your usual';
    accuracy = `Accuracy ${a}${usual}.`;
  }
  const deciding = decidingStage(facts);
  let decided = '';
  if (deciding.kind === 'stage') {
    const lost = deciding.pawns < 0;
    decided = `The ${STAGE_NAME[deciding.stage]} decided it: ${signed(deciding.pawns)}${lost && facts.outcome !== 'loss' ? ', and you held on' : ''}.`;
  } else if (deciding.kind === 'even') {
    decided = 'No stage swung it by a pawn.';
  }
  // The clock: the stage the game ended in, else any stage's time slip.
  const last = (['ending', 'middlegame', 'opening'] as Phase[]).find((s) => facts.stageCurves?.[s]);
  // The result sentence named the pace already.
  const clock = (last && clockNote(last, facts, false)) || (['opening', 'middlegame', 'ending'] as Phase[]).map((s) => clockNote(s, facts, false)).find(Boolean) || '';
  // A loss on time is already the result's first words.
  const lostOnTime = facts.outcome === 'loss' && facts.ending === 'time';
  return { ...sentences(result, accuracy, decided, lostOnTime ? '' : clock), clock: !!clock || lostOnTime };
}

const moveNo = (ply: number) => `${Math.ceil(ply / 2)}${ply % 2 ? '.' : '…'}`;
const move = (m: { ply: number; san: string }) => `${moveNo(m.ply)}${m.san}`;

const PACE: Record<TimeClass, string> = { bullet: 'in bullet', blitz: 'in blitz', rapid: 'in rapid', daily: 'in a daily game' };

/** "in bullet", when the pace explains the clock's part; empty for rapid and daily, where it does not. */
export function paceNote(timeClass: TimeClass | undefined): string {
  return timeClass === 'bullet' || timeClass === 'blitz' ? ` ${PACE[timeClass]}` : '';
}

/** The sentence on the clock for a stage, or empty when it had no part; `pace` false leaves out "in blitz" when the sentence before already named it. */
export function clockNote(stage: Phase, facts: VerdictFacts, pace = true): string {
  const at = pace ? paceNote(facts.timeClass) : '';
  const inStage = (ply: number) => {
    const curve = facts.stageCurves?.[stage];
    return !!curve && ply > curve.fromPly && ply <= curve.toPly;
  };
  const lostOnTime = facts.ending === 'time' && facts.outcome === 'loss' && isLastStage(stage, facts);
  if (lostOnTime) return `You lost on time${at}.`;
  if (facts.timeSlip && inStage(facts.timeSlip.ply) && facts.timeSlip.seconds < TIME_PRESSURE_SECONDS) {
    return `A winning position slipped at ${move(facts.timeSlip)} with ${Math.round(facts.timeSlip.seconds)} seconds left${at}.`;
  }
  if (stage === 'middlegame' && facts.groundCause === 'time') {
    return `Most of it went in moves made with under a minute on the clock${at}.`;
  }
  return '';
}

/** Whether the game ended in this stage: no later stage has a curve. */
function isLastStage(stage: Phase, facts: VerdictFacts): boolean {
  const curves = facts.stageCurves;
  if (!curves?.[stage]) return false;
  if (stage === 'opening') return !curves.middlegame && !curves.ending;
  if (stage === 'middlegame') return !curves.ending;
  return true;
}

const OPENING_WORDS: Record<StageStanding, string> = {
  winning: 'You came out of the opening winning',
  level: 'You came out of the opening level',
  lost: 'You came out of the opening behind',
};

function openingStanding(facts: VerdictFacts): StageStanding | undefined {
  if (facts.stageCurves?.middlegame) return facts.middlegameStart;
  if (!facts.stageCurves?.opening) return undefined;
  return facts.outcome === 'win' ? 'winning' : facts.outcome === 'loss' ? 'lost' : 'level';
}

/** The game's measure against the period's average, in words; empty without an average. */
function versusAverage(value: number | undefined, facts: VerdictFacts): string {
  if (value === undefined || facts.average === undefined || !facts.averageOf) return '';
  const diff = value - facts.average;
  const where = facts.opening ? `in the ${facts.opening}` : 'here';
  if (Math.abs(diff) < 0.25) return `About your usual ${where}.`;
  return `${diff > 0 ? 'Better' : 'Worse'} than your usual ${where} (${facts.average > 0 ? '+' : facts.average < 0 ? '−' : ''}${Math.abs(facts.average).toFixed(2)}).`;
}

/** The sentences given, empty ones dropped, as the verdict's lines and text. */
const sentences = (...parts: string[]): Pick<StageVerdict, 'text' | 'lines'> => {
  const lines = parts.filter(Boolean);
  return { text: lines.join(' '), lines };
};

/** The verdict on a stage of a scanned game; undefined when the game did not reach it or has no classification for it. */
export function stageVerdict(stage: Phase, facts: VerdictFacts): StageVerdict | undefined {
  if (!facts.stageCurves?.[stage]) return undefined;
  const clock = clockNote(stage, facts);
  switch (stage) {
    case 'opening': {
      const standing = openingStanding(facts);
      if (!standing) return undefined;
      const errors = facts.openingErrors;
      const slip = errors?.blunders ? `${errors.blunders === 1 ? 'One blunder' : `${errors.blunders} blunders`} on the way.` : '';
      return { ...sentences(`${OPENING_WORDS[standing]}.`, slip, versusAverage(facts.openingScore, facts), clock), clock: !!clock };
    }
    case 'middlegame': {
      if (!facts.middlegameResult) return undefined;
      const view = middlegameResultView(facts.middlegameResult);
      let decided = '';
      if (facts.groundCause && facts.groundCause !== 'time') {
        const cause = groundCauseView(facts.groundCause);
        const at = facts.groundPractice ? ` at ${move(facts.groundPractice)}` : '';
        decided = `${cause.label === 'Missed chance' ? 'The chance missed' : cause.label === 'Positional' ? 'Ground lost in quiet moves' : 'A tactic'}${at} decided it.`;
      }
      return { ...sentences(view.description, decided, clock), clock: !!clock };
    }
    case 'ending': {
      if (!facts.endingResult) return undefined;
      const view = endingResultView(facts.endingResult);
      const kind = facts.endingType ? ENDING_PHRASE[facts.endingType] : '';
      const kase = facts.endingCase && facts.endingCase !== 'won' && facts.endingCase !== 'drawn' && facts.endingCase !== 'time' ? endingCaseView(facts.endingCase).description : '';
      const what = kind ? `${kind}${facts.endgame?.thrown ? `, a winning position thrown away ${facts.endgame.thrown === 1 ? 'once' : `${facts.endgame.thrown} times`}` : ''}.` : '';
      return { ...sentences(view.description, what, kase, clock), clock: !!clock };
    }
  }
}
